import React, { JSX, useCallback, useMemo, useState } from 'react';
import {
  Info,
  initBookingService,
  LoadingOverlay,
  Overlay,
  OverlayContainer,
  SessionData,
  useAppService,
  useBookingService,
  useConfig,
  useExternalSearchParams,
  useTranslation,
  Warning
} from '@ibe/components';
import PackageSimpleListWrapper from '../../Components/PackageSimpleList/PackageSimpleListWrapper';
import fallback from '../../Translations/generated/product-ibe.de.json';
import Keys from '../../Translations/generated/product-ibe.de.json.keys';
import Filters, { FilterParams, InitialStorageFilters } from './Filters';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import usePackagesListFunctions, { productIBEAnchor } from './usePackagesListFunctions';
import { PromotionCodeData } from '../../Components/PromotionCode';
import {
  BUS_SESSION_STORAGE_KEYS,
  packageFilterSessionStorage,
  packageIdSessionStorage,
  productDetailsPageUrlStorage,
  validPromotionCodeStorage,
  whitelabelSeasonStorage
} from '../Util/utils';
import { Col, Row } from 'reactstrap';
import { useMount } from 'react-use';
import { packageItemSelectEvent } from '../../Tracking/events';
import { accountingAreaStorage, stepSessionStorage } from '../../Util/globals';
import { extendedBookingStorage } from '../Checkout/Components/ExtendedBooking/ContextAndProvider';
import { summaryOverviewSession } from '../Checkout/Components/StickySummary/Content';
import { ApiBookedItem, ApiItemType, ApiUpdateAffiliateRequestFromJSON } from '@ibe/api';
import CustomAnimationSpinner from '../../Components/CustomAnimationSpinner';
import PBusFilters from './PBusFilters';
import {
  AnalyticsService,
  bookingInitEvent,
  CheckoutFailedEvent,
  checkoutFailedEvent,
  LoggerFactory,
  MaskBookingService,
  SessionStoragePersistence
} from '@ibe/services';
import {
  BOOKINGID_STORAGE_KEY,
  MINI_GROUP,
  MINI_GROUP_STR_DE,
  NORMAL_GROUP,
  SMALL_GROUP,
  SMALL_GROUP_STR_DE
} from '../../Util/constant';
import { PackageTypes } from './ProductIBE';
import CustomInfo from '../../Components/CustomInfo';
import {
  DEFAULT_TOUR_OPERATOR,
  TOUR_OPERATOR_TTAG
} from '../../Pages/Checkout/Components/StickySummary/PriceOverviewHeader';
import { ThgConfigModel } from '../../Config/ThgConfigModel';

dayjs.extend(localeData);

const VALID_SORT_FIELDS = ['startDate', 'origin', 'availability', 'price'] as const;
type ConfigurableSortField = typeof VALID_SORT_FIELDS[number];

const isValidSortField = (value?: string): value is ConfigurableSortField =>
  !!value && (VALID_SORT_FIELDS as readonly string[]).includes(value);

const parseIsAscDirection = (value?: string): boolean | undefined => {
  if (value === 'asc') return true;
  if (value === 'desc') return false;
  return undefined;
};

const getInitialSorting = (
  thgConfig: ThgConfigModel | undefined,
  isBus: boolean
): { initialSortProp?: string; initialIsAscDirection?: boolean } => {
  const rawSortProp = isBus
    ? thgConfig?.initialSortIBE?.initialSortPropBus
    : thgConfig?.initialSortIBE?.initialSortPropFlight;

  const rawSortDirection = isBus
    ? thgConfig?.initialSortIBE?.sortDirectionBus
    : thgConfig?.initialSortIBE?.sortDirectionFlight;

  return {
    initialSortProp: isValidSortField(rawSortProp) ? rawSortProp : undefined,
    initialIsAscDirection: parseIsAscDirection(rawSortDirection)
  };
};

export type PackageCode = { code: string; groupSize: string };

export interface ProductIBEProps {
  packageCodes: Array<PackageCode>;
  earliestPossibleDate: string;
  latestPossibleDate: string;
  numberOfTravelers?: number;
  checkoutUrl?: string;
  maintenanceMode?: boolean;
  agencyNumber?: string;
  isWhitelabel?: boolean;
  thgConfig?: ThgConfigModel;
  suppressStrikePrices?: boolean;
}

const logger = LoggerFactory.get('PackagesList');

const PackagesList = (
  props: ProductIBEProps & {
    promotionCode: PromotionCodeData;
    handlePackageSelection?: () => void;
    packageType?: string;
  }
): JSX.Element => {
  const { t } = useTranslation('product-ibe', fallback);
  const {
    earliestPossibleDate,
    latestPossibleDate,
    packageCodes,
    numberOfTravelers,
    promotionCode,
    checkoutUrl,
    maintenanceMode,
    handlePackageSelection,
    agencyNumber,
    isWhitelabel,
    packageType,
    thgConfig,
    suppressStrikePrices
  } = props;
  const config = useConfig();
  const appService = useAppService();
  const bs = useBookingService();

  const [currentNumberOfTravelers, setCurrentNumberOfTravelers] = useState<number>(
    numberOfTravelers || 2
  );

  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const context = useExternalSearchParams();

  const [initialStorageFilters, setInitialStorageFilters] = useState<InitialStorageFilters>({
    initialFilters: undefined,
    initialFiltersChecked: false
  });

  const [requiredCriteriaProvided, setRequiredCriteriaProvided] = useState<boolean>(false);

  useMount(() => {
    const cachedFilter = packageFilterSessionStorage.get();

    const searchParams = new URLSearchParams(window.location.search);
    const initialSizes: string[] = [];

    if (searchParams.has(SMALL_GROUP_STR_DE) && searchParams.get(SMALL_GROUP_STR_DE) !== 'false') {
      initialSizes.push(SMALL_GROUP);
    }
    if (searchParams.has(MINI_GROUP_STR_DE) && searchParams.get(MINI_GROUP_STR_DE) !== 'false') {
      initialSizes.push(MINI_GROUP);
    }

    const urlGroupSize = initialSizes.length > 0 ? initialSizes.join(',') : undefined;

    if (cachedFilter?.packageCodes === JSON.stringify(packageCodes)) {
      setCurrentNumberOfTravelers(cachedFilter.numberOfTravelers);
      setInitialStorageFilters({
        initialFilters: {
          ...cachedFilter,
          // URL params override the cached filter
          groupSize: urlGroupSize || cachedFilter.groupSize
        },
        initialFiltersChecked: true
      });
    } else {
      setInitialStorageFilters({
        initialFilters: urlGroupSize
          ? (({ groupSize: urlGroupSize } as unknown) as InitialStorageFilters['initialFilters'])
          : undefined,
        initialFiltersChecked: true
      });
    }
  });

  const {
    packagesMappingCallback,
    getPackages,
    packages,
    possibleOrigins,
    isLoading,
    initialPackageSearchParams,
    hasSelfArrival,
    onlySelfArrival
  } = usePackagesListFunctions(
    earliestPossibleDate,
    latestPossibleDate,
    packageCodes,
    t,
    promotionCode,
    initialStorageFilters,
    currentNumberOfTravelers,
    maintenanceMode,
    packageType,
    suppressStrikePrices
  );

  const headItems = useMemo(() => {
    return [
      { value: t(Keys.date), sortProp: 'startDate' },
      ...(packages.some(singlePackage => singlePackage.groupSize === SMALL_GROUP)
        ? [{ value: t(Keys.smallGroup), sortProp: SMALL_GROUP }]
        : []),
      ...(packages.some(singlePackage => singlePackage.groupSize === MINI_GROUP) &&
      !packages.some(singlePackage => singlePackage.groupSize === SMALL_GROUP)
        ? [{ value: t(Keys.miniGroup), sortProp: MINI_GROUP }]
        : []),
      packageType === PackageTypes.PBUS
        ? { value: t(Keys.busStop_plural), sortProp: 'origin' }
        : { value: t(Keys.origin), sortProp: 'origin' },
      { value: t(Keys.availability), sortProp: 'availability' },
      { value: t(Keys.pricePerPerson), sortProp: 'price' }
    ];
  }, [packages, packageType]);

  const { initialSortProp, initialIsAscDirection } = useMemo(() => {
    const isBus = packageType === PackageTypes.PBUS;
    return getInitialSorting(thgConfig, isBus);
  }, [thgConfig, packageType]);

  const setAccountingArea = (bookedItems?: ApiBookedItem[] | null) => {
    accountingAreaStorage.clear();
    const packageItem = bookedItems?.find(
      (it: ApiBookedItem) => it.itemType === ApiItemType.PACKAGE
    ) as ApiBookedItem;
    const seasonContent = packageItem?.metaInfo?.contentByMetaType?.season?.content;
    const tourOperator =
      (seasonContent as { tourOperator: string } | undefined)?.tourOperator ??
      DEFAULT_TOUR_OPERATOR;
    if (tourOperator && tourOperator === TOUR_OPERATOR_TTAG) {
      accountingAreaStorage.set(tourOperator);
    }
  };

  const handleFilterChange = useCallback(
    async (filters: FilterParams, ignoreNumberOfTravelers?: boolean): Promise<void> => {
      if (filters.busStations.length > 0 && filters.numberOfTravelers > 0) {
        setRequiredCriteriaProvided(true);
      } else {
        setRequiredCriteriaProvided(false);
      }
      if (!ignoreNumberOfTravelers && filters.numberOfTravelers !== currentNumberOfTravelers) {
        setCurrentNumberOfTravelers(filters.numberOfTravelers);
      }
      await getPackages(filters);
      const cachedFilter = packageFilterSessionStorage.get();
      const finalOrigins = [
        ...new Set([...(cachedFilter?.possibleOrigins ?? []), ...(possibleOrigins ?? [])])
      ];
      const selectedStation = sessionStorage.getItem(BUS_SESSION_STORAGE_KEYS.busStopSelection);
      if (selectedStation) {
        sessionStorage.removeItem(BUS_SESSION_STORAGE_KEYS.busStopSelection);
      }
      packageFilterSessionStorage.set({
        ...filters,
        packageCodes: JSON.stringify(packageCodes),
        earliestPossibleDate,
        latestPossibleDate,
        possibleOrigins: finalOrigins
      });
    },
    [getPackages, currentNumberOfTravelers, promotionCode]
  );

  const handlePackageSelect = useCallback(
    async (id: string, code: string, groupSize?: string, index?: number) => {
      const foundPackage = packages?.find(singlePackage => singlePackage.id === id);
      if (foundPackage) {
        packageItemSelectEvent.broadcast({ item: foundPackage, index: index });
      }
      packageIdSessionStorage.set({
        packageId: id,
        packageCode: code,
        groupSize: groupSize,
        numberOfTravelers: currentNumberOfTravelers,
        stationCodes: foundPackage?.stationCodes,
        stationSupplements: foundPackage?.stationSupplements
      });

      const whitelabelStorage = whitelabelSeasonStorage.get();
      if (whitelabelStorage) {
        whitelabelStorage.groupSize = groupSize ?? NORMAL_GROUP;
        whitelabelSeasonStorage.set(whitelabelStorage);
      }

      if (checkoutUrl) {
        validPromotionCodeStorage.clear();
        productDetailsPageUrlStorage.set(
          `${location.href}${!location.hash ? '#' + productIBEAnchor : ''}`
        );
        location.href = checkoutUrl;
      }

      if (handlePackageSelection) {
        handlePackageSelection();
      }
    },
    [currentNumberOfTravelers, checkoutUrl, packages]
  );

  const checkAvailability = async (id: string): Promise<boolean> => {
    setActionLoading(true);
    let hasError = false;
    try {
      const session = new SessionStoragePersistence<SessionData>(SessionData);
      session.useKey(context?.widgetSessionKeyPrefix + config.sessionKeyCart);

      const packageCartSession = new SessionStoragePersistence<string>();
      packageCartSession.useKey(context?.widgetSessionKeyPrefix + config.sessionKeyPackageCart);

      const bidSessionStorage = new SessionStoragePersistence<string>();
      bidSessionStorage.useKey(context?.widgetSessionKeyPrefix + BOOKINGID_STORAGE_KEY);
      bidSessionStorage.clear();

      if (session.get()?.bookingId && packageCartSession.get()) {
        bs.setApi(appService.api);
        await bs.initPackageCart(id, session.get().bookingId, packageCartSession.get());
        await bs.init(session.get().bookingId);
        bookingInitEvent.broadcast({
          booking: MaskBookingService.maskBooking(bs.booking),
          item: null,
          analytics: AnalyticsService.createAnalyticsObject(bs.booking)
        });
      } else {
        await initBookingService(
          config,
          appService.api,
          true,
          context?.widgetSessionKeyPrefix,
          id,
          true
        );
      }

      if (agencyNumber) {
        await bs.updateAffiliate(
          ApiUpdateAffiliateRequestFromJSON({ affiliateNumber: agencyNumber })
        );
      }
      stepSessionStorage.clear();
      extendedBookingStorage.clear();
      summaryOverviewSession.clear();
      setAccountingArea(bs?.bookedItems);
      if (!bs.packageCart) {
        hasError = true;
      }
    } catch (err) {
      logger.error(err);
      hasError = true;
    } finally {
      setActionLoading(false);
    }
    if (hasError) {
      const foundPackage = packages.find(p => p.id === id);
      checkoutFailedEvent.broadcast(
        new CheckoutFailedEvent(bs.booking, foundPackage, currentNumberOfTravelers)
      );
    }
    return !hasError;
  };

  return (
    <div
      className={
        packages.some(innerSinglePackage => innerSinglePackage.groupSize !== NORMAL_GROUP)
          ? 'group-size-list__container'
          : ''
      }
    >
      <OverlayContainer>
        {packageType === PackageTypes.PBUS ? (
          <PBusFilters
            earliestPossibleDate={earliestPossibleDate}
            latestPossibleDate={latestPossibleDate}
            onChange={handleFilterChange}
            origins={possibleOrigins}
            packageCodes={packageCodes}
            maintenanceMode={maintenanceMode}
            numberOfTravelers={currentNumberOfTravelers}
            packages={packages}
            promotionCode={promotionCode}
            hasSelfArrival={hasSelfArrival}
            onlySelfArrival={onlySelfArrival}
          />
        ) : (
          <Filters
            earliestPossibleDate={earliestPossibleDate}
            latestPossibleDate={latestPossibleDate}
            onChange={handleFilterChange}
            origins={possibleOrigins}
            packageCodes={packageCodes}
            maintenanceMode={maintenanceMode}
            numberOfTravelers={currentNumberOfTravelers}
            packages={packages}
            promotionCode={promotionCode}
            initialPackageSearchParams={initialPackageSearchParams}
          />
        )}
        <LoadingOverlay className="packages-list__loading" isLoading={isLoading}>
          {packages.length > 0 && !maintenanceMode ? (
            <PackageSimpleListWrapper
              headItems={headItems}
              packages={packages}
              onSelect={handlePackageSelect}
              packagesMappingCallback={packagesMappingCallback}
              initialSortProp={initialSortProp}
              initialIsAscDirection={initialIsAscDirection}
              numberOfDisplayedItems={10}
              numberOfInitiallyDisplayedItems={12}
              checkAvailability={checkAvailability}
              errorRowColSpanDiff={1}
            />
          ) : (
            <></>
          )}

          {packages.length === 0 && !maintenanceMode && !isLoading ? (
            <PackageSimpleListWrapper
              headItems={headItems}
              packages={[]}
              onSelect={handlePackageSelect}
              initialSortProp={initialSortProp}
              initialIsAscDirection={initialIsAscDirection}
              staticNonTableContent={
                <Row className="mt-4">
                  <Col>
                    {(packageType === PackageTypes.PFLUG || requiredCriteriaProvided) && (
                      <Info message={t(Keys.noPackagesFound)} />
                    )}
                    {packageType === PackageTypes.PBUS && !requiredCriteriaProvided && (
                      <CustomInfo
                        message={t(Keys.provideSearchCriteria)}
                        messageClass={'bus-package-criteria-info'}
                      />
                    )}
                  </Col>
                </Row>
              }
            />
          ) : (
            <></>
          )}
          {maintenanceMode ? (
            <PackageSimpleListWrapper
              headItems={headItems}
              packages={[]}
              onSelect={handlePackageSelect}
              initialSortProp={initialSortProp}
              initialIsAscDirection={initialIsAscDirection}
              staticNonTableContent={
                <Row className="mt-4">
                  <Col>
                    <Warning message={t(Keys.maintenanceMode)} />
                  </Col>
                </Row>
              }
            />
          ) : (
            <></>
          )}
        </LoadingOverlay>
        {actionLoading && !isWhitelabel && (
          <div className="animated_spinner_container">
            <Overlay customSpinner={<CustomAnimationSpinner packageType={packageType} />} />
          </div>
        )}
        {actionLoading && isWhitelabel && <Overlay />}
      </OverlayContainer>
    </div>
  );
};

export default PackagesList;
